import type { ClassificationClass, DecisionStatus, IssueSeverity } from '@aurora-pvi/contracts';
import type { Rng } from './rng';
import { correlationIdFor, idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type {
  Application,
  ApprovalStep,
  Assessment,
  AssessmentCriterionScore,
  AuditEvent,
  Connector,
  DataQualityIssue,
  DataQualityRule,
  Decision,
  Evidence,
  IngestionRun,
  LineageEdge,
  LineageMapping,
  LineageNode,
  MappingSet,
  Process,
  Product,
  SourceConflict,
} from './types';
import { addDays, at, pad, round, timestampBefore } from './support';

/**
 * Governance, data quality, integration operations and lineage.
 *
 * Grouped together because they are all the same argument in different clothes:
 * a number is only usable if you can see where it came from, who agreed it, how
 * fresh it is and what is currently wrong with it. The conflict and stale-feed
 * records at the bottom of this file exist so that the conflict (UX-S08),
 * stale (UX-S04) and source-failure (UX-S06) page states have genuine data
 * behind them rather than a hand-built mock in a Storybook story.
 */

// ── Assessment criteria ──────────────────────────────────────────────────────

export interface CriterionDefinition {
  criterionId: string;
  ordinal: number;
  name: string;
  guidingQuestion: string;
  weightPercent: number;
  iconToken: string;
  isMandatory: boolean;
  scaleAnchors: { low: string; mid: string; high: string };
}

export const ASSESSMENT_CRITERIA: readonly CriterionDefinition[] = [
  {
    criterionId: 'Q01',
    ordinal: 1,
    name: 'Customer & Outcome Orientation',
    guidingQuestion: 'Does this exist to deliver a named outcome for an identifiable customer?',
    weightPercent: 14,
    iconToken: 'user-target',
    isMandatory: true,
    scaleAnchors: {
      low: 'No identifiable customer; exists to run technology.',
      mid: 'Customer named but outcomes described as deliverables.',
      high: 'Named customer segments with outcomes measured in their terms.',
    },
  },
  {
    criterionId: 'Q02',
    ordinal: 2,
    name: 'Value Stream Alignment',
    guidingQuestion: 'Does it align to a single value stream rather than cutting across many?',
    weightPercent: 12,
    iconToken: 'flow',
    isMandatory: true,
    scaleAnchors: {
      low: 'Spans many value streams with no primary home.',
      mid: 'Primary stream identifiable but boundaries contested.',
      high: 'Clear primary value stream with agreed interfaces.',
    },
  },
  {
    criterionId: 'Q03',
    ordinal: 3,
    name: 'Boundary Clarity',
    guidingQuestion: 'Can what is in and out of scope be stated without argument?',
    weightPercent: 12,
    iconToken: 'frame',
    isMandatory: true,
    scaleAnchors: {
      low: 'Scope described only by the systems it contains.',
      mid: 'Scope drafted; several items still disputed.',
      high: 'In and out of scope agreed, evidenced and dated.',
    },
  },
  {
    criterionId: 'Q04',
    ordinal: 4,
    name: 'Persistent Funding & Team',
    guidingQuestion: 'Is there a standing team and standing funding, rather than a project?',
    weightPercent: 11,
    iconToken: 'people',
    isMandatory: true,
    scaleAnchors: {
      low: 'Funded per project; team assembled and disbanded.',
      mid: 'Standing team, annual funding negotiated each cycle.',
      high: 'Persistent team with multi-year product funding.',
    },
  },
  {
    criterionId: 'Q05',
    ordinal: 5,
    name: 'Ownership & Accountability',
    guidingQuestion: 'Is there a single accountable owner with decision rights?',
    weightPercent: 10,
    iconToken: 'badge',
    isMandatory: true,
    scaleAnchors: {
      low: 'No named owner, or ownership disputed.',
      mid: 'Owner named without full decision rights.',
      high: 'Named owner accountable for outcome and spend.',
    },
  },
  {
    criterionId: 'Q06',
    ordinal: 6,
    name: 'Measurable Outcomes',
    guidingQuestion: 'Are outcomes measured with governed metrics rather than activity counts?',
    weightPercent: 10,
    iconToken: 'gauge',
    isMandatory: false,
    scaleAnchors: {
      low: 'Reports delivery activity only.',
      mid: 'Outcome metrics defined, measurement partial.',
      high: 'Governed outcome metrics reported every period.',
    },
  },
  {
    criterionId: 'Q07',
    ordinal: 7,
    name: 'Lifecycle Longevity',
    guidingQuestion: 'Is this expected to persist beyond the current change agenda?',
    weightPercent: 9,
    iconToken: 'clock',
    isMandatory: false,
    scaleAnchors: {
      low: 'Ends when the current initiative ends.',
      mid: 'Expected to persist but no roadmap beyond 12 months.',
      high: 'Multi-year roadmap with a stated end-of-life position.',
    },
  },
  {
    criterionId: 'Q08',
    ordinal: 8,
    name: 'Technology Coupling',
    guidingQuestion: 'Is the boundary independent of any single application?',
    weightPercent: 8,
    iconToken: 'plug',
    isMandatory: false,
    scaleAnchors: {
      low: 'The boundary is one application.',
      mid: 'Several applications, one dominant.',
      high: 'Capability boundary survives replacing any one system.',
    },
  },
  {
    criterionId: 'Q09',
    ordinal: 9,
    name: 'Demand Independence',
    guidingQuestion: 'Can it prioritise its own backlog against its own outcomes?',
    weightPercent: 8,
    iconToken: 'list',
    isMandatory: false,
    scaleAnchors: {
      low: 'Backlog set entirely by other teams.',
      mid: 'Shared prioritisation with a dominant stakeholder.',
      high: 'Owns prioritisation against published outcomes.',
    },
  },
  {
    criterionId: 'Q10',
    ordinal: 10,
    name: 'Change Cadence',
    guidingQuestion: 'Does it release on its own cadence rather than a shared release train?',
    weightPercent: 6,
    iconToken: 'repeat',
    isMandatory: false,
    scaleAnchors: {
      low: 'Releases only within an enterprise release window.',
      mid: 'Independent for minor change, coupled for major.',
      high: 'Releases independently and continuously.',
    },
  },
];

export const ASSESSMENT_MODEL_VERSION = 'PTM-2026.2';

// ── Evidence ─────────────────────────────────────────────────────────────────

const EVIDENCE_SEEDS: readonly {
  code: string;
  title: string;
  sourceLabel: string;
  daysAgo: number;
  status: Evidence['status'];
  owner: string | null;
}[] = [
  {
    code: 'EVD-CDR-01',
    title: 'Crew Operations target operating model — signed',
    sourceLabel: 'Transformation Office',
    daysAgo: 96,
    status: 'Available',
    owner: 'PPL-0006',
  },
  {
    code: 'EVD-CDR-02',
    title: 'Crew Disruption Recovery product brief v4',
    sourceLabel: 'Product Registry',
    daysAgo: 34,
    status: 'Available',
    owner: 'PPL-0046',
  },
  {
    code: 'EVD-CDR-03',
    title: 'Crew agreement duty-limit schedule (extract)',
    sourceLabel: 'People & Workplace',
    daysAgo: 210,
    status: 'Restricted',
    owner: 'PPL-0032',
  },
  {
    code: 'EVD-CDR-04',
    title: 'FY2026 crew disruption cost baseline',
    sourceLabel: 'Finance ERP',
    daysAgo: 58,
    status: 'Available',
    owner: 'PPL-0058',
  },
  {
    code: 'EVD-CDR-05',
    title: 'Crew systems application inventory extract',
    sourceLabel: 'Aurora CMDB',
    daysAgo: 12,
    status: 'Available',
    owner: 'PPL-0013',
  },
  {
    code: 'EVD-CDR-06',
    title: 'Day-of-ops process walkthrough notes',
    sourceLabel: 'Operations Control Centre',
    daysAgo: 141,
    status: 'Superseded',
    owner: 'PPL-0055',
  },
  {
    code: 'EVD-CDR-07',
    title: 'Crew recovery benefits case — approved',
    sourceLabel: 'Finance ERP',
    daysAgo: 77,
    status: 'Available',
    owner: 'PPL-0003',
  },
  {
    code: 'EVD-CDR-08',
    title: 'Vendor managed-service scope (Northwind Digital)',
    sourceLabel: 'Contract Manager',
    daysAgo: 45,
    status: 'Restricted',
    owner: 'PPL-0011',
  },
];

// ── Data quality rules ───────────────────────────────────────────────────────

const DQ_RULE_SEEDS: readonly {
  code: string;
  dimension: DataQualityRule['dimension'];
  name: string;
  entityType: string;
}[] = [
  {
    code: 'DQ-001',
    dimension: 'Completeness',
    name: 'Every application has a named owner',
    entityType: 'Application',
  },
  {
    code: 'DQ-002',
    dimension: 'Completeness',
    name: 'Every product has a business owner',
    entityType: 'Product',
  },
  {
    code: 'DQ-003',
    dimension: 'Completeness',
    name: 'Every product has at least one mapped process',
    entityType: 'Product',
  },
  {
    code: 'DQ-004',
    dimension: 'Completeness',
    name: 'Every application maps to at least one product',
    entityType: 'Application',
  },
  {
    code: 'DQ-005',
    dimension: 'Completeness',
    name: 'Every cost actual carries a cost object',
    entityType: 'CostActual',
  },
  {
    code: 'DQ-006',
    dimension: 'Validity',
    name: 'Lifecycle values are drawn from the controlled vocabulary',
    entityType: 'Product',
  },
  {
    code: 'DQ-007',
    dimension: 'Validity',
    name: 'Criticality values are drawn from the controlled vocabulary',
    entityType: 'Application',
  },
  {
    code: 'DQ-008',
    dimension: 'Validity',
    name: 'Health score is within 0–100',
    entityType: 'Product',
  },
  {
    code: 'DQ-009',
    dimension: 'Validity',
    name: 'Allocation share is within 0–1',
    entityType: 'CostAllocation',
  },
  {
    code: 'DQ-010',
    dimension: 'Uniqueness',
    name: 'Business codes are unique within entity type',
    entityType: 'All',
  },
  {
    code: 'DQ-011',
    dimension: 'Uniqueness',
    name: 'No duplicate product-to-application relationship',
    entityType: 'Relationship',
  },
  {
    code: 'DQ-012',
    dimension: 'Consistency',
    name: 'Allocated shares for a cost object never exceed 100%',
    entityType: 'CostAllocation',
  },
  {
    code: 'DQ-013',
    dimension: 'Consistency',
    name: 'Health band agrees with health score',
    entityType: 'Product',
  },
  {
    code: 'DQ-014',
    dimension: 'Consistency',
    name: 'Product hierarchy parent exists and is effective',
    entityType: 'Product',
  },
  {
    code: 'DQ-015',
    dimension: 'Consistency',
    name: 'Cost breakdown sums to the reported total',
    entityType: 'CostActual',
  },
  {
    code: 'DQ-016',
    dimension: 'Timeliness',
    name: 'Source refreshed within its declared frequency',
    entityType: 'All',
  },
  {
    code: 'DQ-017',
    dimension: 'Timeliness',
    name: 'Assessment reviewed within 12 months',
    entityType: 'Assessment',
  },
  {
    code: 'DQ-018',
    dimension: 'Accuracy',
    name: 'Application run cost agrees between CMDB and Finance ERP',
    entityType: 'Application',
  },
  {
    code: 'DQ-019',
    dimension: 'Accuracy',
    name: 'Owner in HCM Suite matches owner in the product registry',
    entityType: 'Product',
  },
  {
    code: 'DQ-020',
    dimension: 'Completeness',
    name: 'Every contract records a notice period',
    entityType: 'Contract',
  },
];

// ── Connectors ───────────────────────────────────────────────────────────────

const CONNECTOR_SEEDS: readonly {
  integrationId: string;
  name: string;
  category: string;
  mode: Connector['mode'];
  health: Connector['connectionHealth'];
  auth: Connector['authenticationStatus'];
  authMethod: string;
  syncHoursAgo: number | null;
  owner: string | null;
  quality: number | null;
}[] = [
  {
    integrationId: 'INT-001',
    name: 'Aurora CMDB',
    category: 'Configuration',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 2,
    owner: 'PPL-0013',
    quality: 0.94,
  },
  {
    integrationId: 'INT-002',
    name: 'Aurora Data Hub',
    category: 'Data Platform',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'Workload identity federation',
    syncHoursAgo: 1,
    owner: 'PPL-0007',
    quality: 0.97,
  },
  {
    integrationId: 'INT-003',
    name: 'Work Tracker',
    category: 'Delivery',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Expiring',
    authMethod: 'Personal access token',
    syncHoursAgo: 9,
    owner: 'PPL-0055',
    quality: 0.97,
  },
  {
    integrationId: 'INT-004',
    name: 'Service Desk',
    category: 'Operations',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 authorisation code',
    syncHoursAgo: 3,
    owner: 'PPL-0057',
    quality: 0.98,
  },
  {
    integrationId: 'INT-005',
    name: 'Finance ERP',
    category: 'Finance',
    mode: 'FileFeed',
    health: 'Down',
    auth: 'Failed',
    authMethod: 'SFTP key pair',
    syncHoursAgo: 74,
    owner: 'PPL-0014',
    quality: 0.70,
  },
  {
    integrationId: 'INT-006',
    name: 'HCM Suite',
    category: 'Workforce',
    mode: 'FileFeed',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'SFTP key pair',
    syncHoursAgo: 20,
    owner: 'PPL-0012',
    quality: 0.96,
  },
  {
    integrationId: 'INT-007',
    name: 'Procurement Cloud',
    category: 'Sourcing',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 14,
    owner: 'PPL-0010',
    quality: 0.97,
  },
  {
    integrationId: 'INT-008',
    name: 'Contract Manager',
    category: 'Sourcing',
    mode: 'ManagedFileDrop',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'Managed drop with checksum',
    syncHoursAgo: 52,
    owner: 'PPL-0011',
    quality: 0.96,
  },
  {
    integrationId: 'INT-009',
    name: 'Observability Platform',
    category: 'Operations',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'API key (rotated)',
    syncHoursAgo: 1,
    owner: 'PPL-0057',
    quality: 0.98,
  },
  {
    integrationId: 'INT-010',
    name: 'Product Registry',
    category: 'Portfolio',
    mode: 'ManagedFileDrop',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'Managed drop with checksum',
    syncHoursAgo: 26,
    owner: 'PPL-0008',
    quality: 0.97,
  },
  {
    integrationId: 'INT-011',
    name: 'Enterprise Identity',
    category: 'Identity',
    mode: 'Identity',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'SAML 2.0 / SCIM',
    syncHoursAgo: 6,
    owner: 'PPL-0007',
    quality: null,
  },
  {
    integrationId: 'INT-012',
    name: 'Notification Service',
    category: 'Notification',
    mode: 'Notification',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 3,
    owner: 'PPL-0007',
    quality: null,
  },
  // A carrier of this size runs far more than twelve integrations, and at
  // twelve a single failed source drags healthy-connection share to 83%,
  // which reads as a troubled platform rather than one source that needs
  // attention. These eight are the feeds an airline obviously has and the
  // original set was simply missing.
  {
    integrationId: 'INT-013',
    name: 'Crew Management System',
    category: 'Workforce',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 4,
    owner: 'PPL-0012',
    quality: 0.96,
  },
  {
    integrationId: 'INT-014',
    name: 'Flight Operations Data Store',
    category: 'Operations',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'Workload identity federation',
    syncHoursAgo: 1,
    owner: 'PPL-0057',
    quality: 0.98,
  },
  {
    integrationId: 'INT-015',
    name: 'Revenue Accounting',
    category: 'Finance',
    mode: 'FileFeed',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'SFTP key pair',
    syncHoursAgo: 18,
    owner: 'PPL-0014',
    quality: 0.95,
  },
  {
    integrationId: 'INT-016',
    name: 'Maintenance & Engineering',
    category: 'Operations',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'API key (rotated)',
    syncHoursAgo: 5,
    owner: 'PPL-0057',
    quality: 0.94,
  },
  {
    integrationId: 'INT-017',
    name: 'Cloud Cost Management',
    category: 'Finance',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 2,
    owner: 'PPL-0014',
    quality: 0.97,
  },
  {
    integrationId: 'INT-018',
    name: 'Source Control Platform',
    category: 'Delivery',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 app installation',
    syncHoursAgo: 1,
    owner: 'PPL-0055',
    quality: 0.99,
  },
  {
    integrationId: 'INT-019',
    name: 'Loyalty Platform',
    category: 'Commercial',
    mode: 'DirectConnector',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'OAuth 2.0 client credentials',
    syncHoursAgo: 6,
    owner: 'PPL-0010',
    quality: 0.95,
  },
  {
    integrationId: 'INT-020',
    name: 'Enterprise Risk Register',
    category: 'Governance',
    mode: 'ManagedFileDrop',
    health: 'Healthy',
    auth: 'Configured',
    authMethod: 'Managed drop with checksum',
    syncHoursAgo: 30,
    owner: 'PPL-0011',
    quality: 0.93,
  },
];

const ERROR_TYPES = [
  'Referential integrity — unknown parent code',
  'Required field missing — owner',
  'Controlled vocabulary — unrecognised lifecycle value',
  'Type parsing — non-numeric cost amount',
  'Uniqueness — duplicate business code',
  'Effective dates — validTo precedes validFrom',
];

export interface GovernanceInputs {
  asOf: string;
  rng: Rng;
  products: readonly Product[];
  applications: readonly Application[];
  processes: readonly Process[];
}

export interface GovernanceData {
  evidence: Evidence[];
  assessments: Assessment[];
  decisions: Decision[];
  approvals: ApprovalStep[];
  auditEvents: AuditEvent[];
  dataQualityRules: DataQualityRule[];
  dataQualityIssues: DataQualityIssue[];
  sourceConflicts: SourceConflict[];
  mappingSets: MappingSet[];
  connectors: Connector[];
  ingestionRuns: IngestionRun[];
  lineageNodes: LineageNode[];
  lineageEdges: LineageEdge[];
  lineageMappings: LineageMapping[];
}

export function buildGovernance(inputs: GovernanceInputs): GovernanceData {
  const { asOf, products, applications } = inputs;
  const rng = inputs.rng.fork('governance');
  const stamp = provenanceFactory(asOf, inputs.rng.fork('governance-provenance'));

  // ── Evidence ───────────────────────────────────────────────────────────────
  const evidence: Evidence[] = EVIDENCE_SEEDS.map((seed) => ({
    id: idFor('Evidence', seed.code),
    businessCode: seed.code,
    title: seed.title,
    sourceLabel: seed.sourceLabel,
    date: addDays(asOf, -seed.daysAgo),
    status: seed.status,
    url: seed.status === 'Available' ? `/evidence/${seed.code.toLowerCase()}` : null,
    ownerPersonCode: seed.owner,
    provenance: stamp(SOURCE_SYSTEMS.manual, `EV-${seed.code}`, {
      meanAgeDays: seed.daysAgo,
      meanConfidence: seed.status === 'Available' ? 0.9 : 0.6,
    }),
  }));

  // Generic evidence referenced by relationship records.
  for (const kind of ['PA', 'PP', 'AP', 'TP', 'PD']) {
    for (let i = 1; i <= 40; i += 1) {
      const code = `EVD-${kind}-${pad(i, 3)}`;
      evidence.push({
        id: idFor('Evidence', code),
        businessCode: code,
        title: `Mapping evidence ${code}`,
        sourceLabel: 'Aurora Data Hub',
        date: addDays(asOf, -rng.int(5, 240)),
        status: 'Available',
        url: `/evidence/${code.toLowerCase()}`,
        ownerPersonCode: 'PPL-0013',
        provenance: stamp(SOURCE_SYSTEMS.dataHub, `EV-${code}`, {
          meanAgeDays: 30,
          meanConfidence: 0.78,
        }),
      });
    }
  }

  // ── Assessments ────────────────────────────────────────────────────────────
  const assessments: Assessment[] = [];
  const decisions: Decision[] = [];
  const approvals: ApprovalStep[] = [];

  const fullScores = (values: readonly number[]): AssessmentCriterionScore[] =>
    ASSESSMENT_CRITERIA.map((criterion, index) => ({
      criterionId: criterion.criterionId,
      score: (values[index] ?? null) as AssessmentCriterionScore['score'],
      rationale:
        values[index] === undefined
          ? null
          : `Scored ${values[index]} of 5 against the ${criterion.name.toLowerCase()} anchors.`,
      evidenceCodes: index < 3 ? ['EVD-CDR-02', 'EVD-CDR-05'] : index < 6 ? ['EVD-CDR-01'] : [],
    }));

  // The completed, approved assessment behind the results screen.
  assessments.push({
    id: idFor('Assessment', 'ASM-0001'),
    businessCode: 'ASM-0001',
    candidateCode: 'CND-CDR',
    candidateName: 'Crew Disruption Recovery',
    productCode: 'PRD-CDR',
    domain: 'Flight Operations',
    processContext: 'PRC-5.4 Recover Disrupted Crew',
    valueStreamCode: 'VS-CREW',
    l2DomainCode: 'PRC-5.4',
    ownerPersonCode: 'PPL-0046',
    status: 'Approved',
    modelVersion: ASSESSMENT_MODEL_VERSION,
    currentStep: 'Review',
    completedSteps: ['Overview', 'Attributes', 'Evidence', 'Classification', 'Review'],
    scores: fullScores([5, 5, 4, 5, 5, 4, 4, 3, 4, 4]),
    recommended: 'BusinessProduct',
    warnings: [
      {
        antiPattern: 'PlatformMasqueradingAsBusinessProduct',
        message:
          'Three of the six in-scope capabilities are consumed by other products. Confirm the boundary is a business outcome and not a shared platform.',
        severity: 'Warning',
      },
    ],
    completedAt: timestampBefore(asOf, 26, 540),
    completedByPersonCode: 'PPL-0046',
    lastSavedAt: timestampBefore(asOf, 26, 540),
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, 'PR-ASM-0001', {
      meanAgeDays: 26,
      meanConfidence: 0.93,
    }),
  });

  // The in-progress assessment behind the wizard screen — deliberately partial:
  // two criteria unanswered, so the wizard can show "not yet assessed" rather
  // than defaulting an unanswered question to the lowest score.
  assessments.push({
    id: idFor('Assessment', 'ASM-0002'),
    businessCode: 'ASM-0002',
    candidateCode: 'CND-IROP',
    candidateName: 'Integrated Irregular Operations Command',
    productCode: null,
    domain: 'Flight Operations',
    processContext: 'PRC-4.3 Control the Operation',
    valueStreamCode: 'VS-FLTOPS',
    l2DomainCode: 'PRC-4.3',
    ownerPersonCode: 'PPL-0021',
    status: 'InReview',
    modelVersion: ASSESSMENT_MODEL_VERSION,
    currentStep: 'Classification',
    completedSteps: ['Overview', 'Attributes', 'Evidence'],
    scores: ASSESSMENT_CRITERIA.map((criterion, index) => ({
      criterionId: criterion.criterionId,
      score: (index >= 8
        ? null
        : at([4, 3, 2, 3, 4, 3, 3, 2], index)) as AssessmentCriterionScore['score'],
      rationale: index >= 8 ? null : 'Reviewed with the value stream owner on 6 May.',
      evidenceCodes: index < 2 ? ['EVD-CDR-06'] : [],
    })),
    recommended: 'Undetermined',
    warnings: [
      {
        antiPattern: 'ProductTooBroad',
        message:
          'The proposed boundary spans four value streams. Consider splitting along the operational decision it owns.',
        severity: 'Error',
      },
      {
        antiPattern: 'ProjectMasqueradingAsProduct',
        message:
          'Funding is currently drawn from a programme business case that ends in March 2027.',
        severity: 'Warning',
      },
    ],
    completedAt: null,
    completedByPersonCode: null,
    lastSavedAt: timestampBefore(asOf, 0, 187),
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, 'PR-ASM-0002', {
      meanAgeDays: 0,
      meanConfidence: 0.66,
    }),
  });

  // Assessments for the candidate tail.
  const candidates = products.filter(
    (p) => p.lifecycle === 'Candidate' || p.lifecycle === 'Proposed',
  );
  candidates.forEach((product, index) => {
    const code = `ASM-${pad(index + 3, 4)}`;
    const status = at(
      ['Draft', 'Submitted', 'InReview', 'ChangesRequested', 'Approved'] as DecisionStatus[],
      index % 5,
    );
    const recommended = at(
      [
        'BusinessProduct',
        'PlatformProduct',
        'SharedService',
        'DataProduct',
        'ApplicationComponent',
        'Undetermined',
      ] as ClassificationClass[],
      index % 6,
    );
    assessments.push({
      id: idFor('Assessment', code),
      businessCode: code,
      candidateCode: `CND-${pad(index + 1, 4)}`,
      candidateName: product.name,
      productCode: product.businessCode,
      domain: product.departmentCode,
      processContext: 'To be confirmed with the value stream owner.',
      valueStreamCode: product.valueStreamCode,
      l2DomainCode: 'PRC-8.6',
      ownerPersonCode: product.ownerPersonCode,
      status,
      modelVersion: ASSESSMENT_MODEL_VERSION,
      currentStep:
        status === 'Draft' ? 'Attributes' : status === 'Approved' ? 'Review' : 'Evidence',
      completedSteps: status === 'Draft' ? ['Overview'] : ['Overview', 'Attributes'],
      scores: ASSESSMENT_CRITERIA.map((criterion, i) => ({
        criterionId: criterion.criterionId,
        score: (status === 'Draft' && i > 4
          ? null
          : rng.int(2, 5)) as AssessmentCriterionScore['score'],
        rationale: null,
        evidenceCodes: [],
      })),
      recommended,
      warnings:
        index % 3 === 0
          ? [
              {
                antiPattern: 'ApplicationEqualsProduct',
                message: 'The proposed boundary is currently a single application.',
                severity: 'Warning' as IssueSeverity,
              },
            ]
          : [],
      completedAt: status === 'Approved' ? timestampBefore(asOf, rng.int(10, 200)) : null,
      completedByPersonCode: status === 'Approved' ? product.ownerPersonCode : null,
      lastSavedAt: timestampBefore(asOf, rng.int(0, 60), rng.int(0, 1400)),
      provenance: stamp(SOURCE_SYSTEMS.productRegistry, `PR-${code}`, {
        meanAgeDays: rng.int(1, 70),
        meanConfidence: rng.float(0.5, 0.9),
      }),
    });
  });

  // ── Decisions and approvals ────────────────────────────────────────────────
  const pushApproval = (
    decisionCode: string,
    stage: string,
    person: string | null,
    action: ApprovalStep['action'],
    daysAgo: number,
    comments: string | null,
    effectiveDate: string | null = null,
  ): void => {
    const code = `APV-${decisionCode}-${stage.replace(/\s+/g, '')}`;
    approvals.push({
      id: idFor('Approval', code),
      businessCode: code,
      decisionCode,
      stage,
      actorPersonCode: person,
      action,
      occurredAt: timestampBefore(asOf, daysAgo, rng.int(0, 900)),
      comments,
      effectiveDate,
    });
  };

  decisions.push({
    id: idFor('Decision', 'DEC-0001'),
    businessCode: 'DEC-0001',
    subjectKind: 'Assessment',
    subjectCode: 'ASM-0001',
    title: 'Classify Crew Disruption Recovery as a Business Product',
    status: 'Approved',
    decidedOn: addDays(asOf, -24),
    deciderPersonCode: 'PPL-0015',
    rationale:
      'Owns a named operational outcome for crew and the operation, has persistent funding and a standing team, and its boundary survives replacing any single crew system.',
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, 'PR-DEC-0001', {
      meanAgeDays: 24,
      meanConfidence: 0.95,
    }),
  });
  pushApproval(
    'DEC-0001',
    'Submitted',
    'PPL-0046',
    'Submitted',
    34,
    'Assessment complete with evidence attached.',
  );
  pushApproval(
    'DEC-0001',
    'Architecture Review',
    'PPL-0005',
    'Reviewed',
    30,
    'Boundary agreed; platform overlap noted as a risk to monitor.',
  );
  pushApproval(
    'DEC-0001',
    'Value Stream Review',
    'PPL-0020',
    'Reviewed',
    27,
    'Supported. Reserve management stays in scope.',
  );
  pushApproval(
    'DEC-0001',
    'Portfolio Board',
    'PPL-0015',
    'Approved',
    24,
    'Approved as a Tier 1 business product.',
    addDays(asOf, -20),
  );
  pushApproval('DEC-0001', 'Published', 'PPL-0008', 'Published', 20, null, addDays(asOf, -20));

  decisions.push({
    id: idFor('Decision', 'DEC-0002'),
    businessCode: 'DEC-0002',
    subjectKind: 'Assessment',
    subjectCode: 'ASM-0002',
    title: 'Classify Integrated Irregular Operations Command',
    status: 'InReview',
    decidedOn: null,
    deciderPersonCode: null,
    rationale: 'Awaiting a boundary split proposal before the classification can be settled.',
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, 'PR-DEC-0002', {
      meanAgeDays: 3,
      meanConfidence: 0.6,
    }),
  });
  pushApproval(
    'DEC-0002',
    'Submitted',
    'PPL-0021',
    'Submitted',
    9,
    'Submitted for classification.',
  );
  pushApproval(
    'DEC-0002',
    'Architecture Review',
    'PPL-0005',
    'Returned',
    3,
    'Boundary spans four value streams — please split and resubmit.',
  );

  decisions.push({
    id: idFor('Decision', 'DEC-0003'),
    businessCode: 'DEC-0003',
    subjectKind: 'Application',
    subjectCode: 'APP-0034',
    title: 'Consolidate ProrateEngine into RevAcct Ledger',
    status: 'Submitted',
    decidedOn: null,
    deciderPersonCode: null,
    rationale: 'Capability overlap of 0.74 with an agreed replacement path already funded.',
    provenance: stamp(SOURCE_SYSTEMS.cmdb, 'CMDB-DEC-0003', {
      meanAgeDays: 6,
      meanConfidence: 0.72,
    }),
  });
  pushApproval('DEC-0003', 'Submitted', 'PPL-0005', 'Submitted', 6, 'Duplicate analysis attached.');

  // ── Mapping sets ───────────────────────────────────────────────────────────
  const mappingSets: MappingSet[] = [
    {
      id: idFor('MappingSet', 'MAP-2026-Q2'),
      businessCode: 'MAP-2026-Q2',
      name: 'Process-to-Product Mapping — FY2026 Q2',
      version: 4,
      status: 'Published',
      lastEditedAt: timestampBefore(asOf, 5, 420),
      ownerPersonCode: 'PPL-0008',
    },
    {
      id: idFor('MappingSet', 'MAP-2026-Q3'),
      businessCode: 'MAP-2026-Q3',
      name: 'Process-to-Product Mapping — FY2026 Q3 (draft)',
      version: 5,
      status: 'Draft',
      lastEditedAt: timestampBefore(asOf, 0, 96),
      ownerPersonCode: 'PPL-0008',
    },
  ];

  // ── Data quality ───────────────────────────────────────────────────────────
  const dataQualityRules: DataQualityRule[] = DQ_RULE_SEEDS.map((seed, index) => {
    const outcome: DataQualityRule['outcome'] =
      index % 9 === 4 ? 'Failed' : index % 13 === 7 ? 'Waived' : 'Passed';
    return {
      ruleCode: seed.code,
      dimension: seed.dimension,
      name: seed.name,
      entityType: seed.entityType,
      outcome,
      affectedRecords: outcome === 'Passed' ? 0 : rng.int(3, 184),
    };
  });

  const dataQualityIssues: DataQualityIssue[] = [];
  const orphanApps = applications.filter((a) => !a.isCurated).slice(0, 40);
  orphanApps.forEach((app, index) => {
    const code = `DQI-${pad(index + 1, 5)}`;
    dataQualityIssues.push({
      id: idFor('DataQualityIssue', code),
      businessCode: code,
      ruleCode: at(['DQ-004', 'DQ-001', 'DQ-016', 'DQ-018'], index % 4),
      severity: at(['Error', 'Warning', 'Warning', 'Info'] as IssueSeverity[], index % 4),
      entityLabel: `${app.name} (${app.businessCode})`,
      entityCode: app.businessCode,
      issue: at(
        [
          'Application has no approved product mapping.',
          'Application owner is vacant in the source system.',
          'Source record has not refreshed within its declared frequency.',
          'Annual run cost differs between Aurora CMDB and Finance ERP.',
        ],
        index % 4,
      ),
      raisedOn: addDays(asOf, -rng.int(1, 90)),
      status: at(['Open', 'Open', 'InProgress', 'Waived'] as const, index % 4),
      provenance: stamp(SOURCE_SYSTEMS.dataHub, `DQ-${code}`, {
        meanAgeDays: rng.int(1, 60),
        meanConfidence: 0.83,
      }),
    });
  });

  // ── Source conflicts ───────────────────────────────────────────────────────
  const sourceConflicts: SourceConflict[] = [
    {
      id: idFor('SourceConflict', 'CNF-0001'),
      businessCode: 'CNF-0001',
      entityType: 'Application',
      entityCode: 'APP-0005',
      field: 'annualRunCost',
      claims: [
        {
          sourceSystem: SOURCE_SYSTEMS.cmdb,
          value: 'USD 24,800,000',
          observedAt: timestampBefore(asOf, 4, 210),
          confidence: 0.78,
        },
        {
          sourceSystem: SOURCE_SYSTEMS.financeErp,
          value: 'USD 27,340,000',
          observedAt: timestampBefore(asOf, 11, 60),
          confidence: 0.91,
        },
      ],
      status: 'Open',
      resolvedValue: null,
    },
    {
      id: idFor('SourceConflict', 'CNF-0002'),
      businessCode: 'CNF-0002',
      entityType: 'Application',
      entityCode: 'APP-0033',
      field: 'applicationOwner',
      claims: [
        {
          sourceSystem: SOURCE_SYSTEMS.cmdb,
          value: 'Mateo Guzmán',
          observedAt: timestampBefore(asOf, 2, 130),
          confidence: 0.7,
        },
        {
          sourceSystem: SOURCE_SYSTEMS.hcm,
          value: 'Bernadette Chu',
          observedAt: timestampBefore(asOf, 1, 400),
          confidence: 0.86,
        },
      ],
      status: 'Open',
      resolvedValue: null,
    },
    {
      id: idFor('SourceConflict', 'CNF-0003'),
      businessCode: 'CNF-0003',
      entityType: 'Product',
      entityCode: 'PRD-REVA',
      field: 'lifecycle',
      claims: [
        {
          sourceSystem: SOURCE_SYSTEMS.productRegistry,
          value: 'Active',
          observedAt: timestampBefore(asOf, 6, 300),
          confidence: 0.88,
        },
        {
          sourceSystem: SOURCE_SYSTEMS.manual,
          value: 'Sunset',
          observedAt: timestampBefore(asOf, 33, 20),
          confidence: 0.55,
        },
      ],
      status: 'Resolved',
      resolvedValue: 'Active',
    },
  ];

  // ── Connectors and ingestion runs ──────────────────────────────────────────
  const connectors: Connector[] = CONNECTOR_SEEDS.map((seed) => {
    const lastSyncAt =
      seed.syncHoursAgo === null ? null : timestampBefore(asOf, 0, -seed.syncHoursAgo * 60);
    return {
      id: idFor('Connector', seed.integrationId),
      integrationId: seed.integrationId,
      name: seed.name,
      category: seed.category,
      mode: seed.mode,
      connectionHealth: seed.health,
      lastSyncAt,
      objectsIngested: seed.health === 'Paused' ? 0 : rng.int(3, 22),
      recordsIngested: seed.health === 'Paused' ? 0 : rng.int(1_200, 486_000),
      errorQueueCount:
        seed.health === 'Down'
          ? rng.int(180, 640)
          : seed.health === 'Degraded'
            ? rng.int(12, 96)
            : rng.int(0, 8),
      dataQualityPercent: seed.quality,
      authenticationStatus: seed.auth,
      authenticationMethod: seed.authMethod ?? null,
      nextRefreshAt:
        seed.health === 'Paused' ? null : timestampBefore(asOf, 0, -(rng.int(1, 22) * 60)),
      ownerPersonCode: seed.owner,
      // 48 hours is the freshness threshold the shell uses to decide "stale".
      isStale: seed.syncHoursAgo !== null && seed.syncHoursAgo > 48,
    };
  });

  const ingestionRuns: IngestionRun[] = [];
  connectors.forEach((connector, connectorIndex) => {
    const runCount = connector.connectionHealth === 'Paused' ? 1 : rng.int(2, 5);
    for (let i = 0; i < runCount; i += 1) {
      const seq = ingestionRuns.length + 1;
      const submitted = rng.int(400, 42_000);
      const rejected =
        connector.connectionHealth === 'Down'
          ? Math.round(submitted * rng.float(0.3, 0.9))
          : Math.round(submitted * rng.float(0, 0.04));
      const warning = Math.round((submitted - rejected) * rng.float(0, 0.09));
      const valid = submitted - rejected - warning;
      const unchanged = Math.round(valid * rng.float(0.4, 0.8));
      const created = Math.round((valid - unchanged) * rng.float(0.1, 0.4));
      const updated = valid - unchanged - created;
      const status: IngestionRun['status'] =
        connector.connectionHealth === 'Down' && i === 0
          ? 'Failed'
          : connector.connectionHealth === 'Paused'
            ? 'Cancelled'
            : warning > 0
              ? 'Warning'
              : 'Completed';
      const runCode = `RUN-${pad(seq, 5)}`;
      ingestionRuns.push({
        id: idFor('IngestionRun', runCode),
        runCode,
        connectorIntegrationId: connector.integrationId,
        fileName:
          connector.mode === 'FileFeed' || connector.mode === 'ManagedFileDrop'
            ? `${connector.name.toLowerCase().replace(/\s+/g, '-')}-${addDays(asOf, -i)}.csv`
            : null,
        actorPersonCode: connector.ownerPersonCode,
        startedAt: timestampBefore(asOf, i, rng.int(0, 1400) + connectorIndex * 7),
        durationMs: status === 'Cancelled' ? null : rng.int(4_000, 1_900_000),
        status,
        counts: {
          submitted,
          read: submitted,
          valid,
          warning,
          rejected,
          unchanged,
          created,
          updated,
        },
        errorTypes:
          rejected === 0
            ? []
            : rng.sample(ERROR_TYPES, rng.int(1, 3)).map((errorType, index) => ({
                errorType,
                count: Math.max(1, Math.round(rejected / (index + 2))),
              })),
      });
    }
  });

  // ── Lineage ────────────────────────────────────────────────────────────────
  const lineageNodeSeeds: readonly [
    string,
    LineageNode['column'],
    string,
    string | null,
    number | null,
    LineageNode['status'],
    string,
  ][] = [
    [
      'LN-SRC-CMDB-APP',
      'source',
      'Aurora CMDB — ci_application',
      'Nightly extract',
      1900,
      'Active',
      'database',
    ],
    [
      'LN-SRC-ERP-COST',
      'source',
      'Finance ERP — gl_cost_actual',
      'Monthly file feed',
      48_210,
      'Error',
      'file',
    ],
    [
      'LN-SRC-HCM-ORG',
      'source',
      'HCM Suite — org_unit',
      'Weekly extract',
      1_284,
      'Active',
      'people',
    ],
    [
      'LN-SRC-WT-ITEM',
      'source',
      'Work Tracker — work_item',
      'Streaming',
      96_420,
      'Active',
      'stream',
    ],
    [
      'LN-STG-APP',
      'staging',
      'stg_application',
      'Typed and de-duplicated',
      1900,
      'Active',
      'layers',
    ],
    [
      'LN-STG-COST',
      'staging',
      'stg_cost_actual',
      'Typed, currency-normalised',
      47_866,
      'Draft',
      'layers',
    ],
    ['LN-STG-ORG', 'staging', 'stg_org_unit', 'Typed and de-duplicated', 1_284, 'Active', 'layers'],
    ['LN-CAN-APP', 'canonical', 'canonical.application', 'Governed entity', 1900, 'Active', 'cube'],
    [
      'LN-CAN-COST',
      'canonical',
      'canonical.cost_actual',
      'Governed entity',
      47_866,
      'Active',
      'cube',
    ],
    [
      'LN-CAN-ORG',
      'canonical',
      'canonical.organizational_unit',
      'Governed entity',
      1_284,
      'Active',
      'cube',
    ],
  ];

  const lineageNodes: LineageNode[] = lineageNodeSeeds.map(
    ([code, column, label, sublabel, recordCount, status, iconToken]) => ({
      id: idFor('LineageNode', code),
      code,
      column,
      label,
      sublabel,
      recordCount,
      status,
      iconToken,
    }),
  );

  const lineageEdgeSeeds: readonly [
    string,
    string,
    string,
    string | null,
    LineageEdge['status'],
  ][] = [
    ['LE-01', 'LN-SRC-CMDB-APP', 'LN-STG-APP', 'MR-APP-01', 'Active'],
    ['LE-02', 'LN-STG-APP', 'LN-CAN-APP', 'MR-APP-02', 'Active'],
    ['LE-03', 'LN-SRC-ERP-COST', 'LN-STG-COST', 'MR-COST-01', 'Error'],
    ['LE-04', 'LN-STG-COST', 'LN-CAN-COST', 'MR-COST-02', 'Draft'],
    ['LE-05', 'LN-SRC-HCM-ORG', 'LN-STG-ORG', 'MR-ORG-01', 'Active'],
    ['LE-06', 'LN-STG-ORG', 'LN-CAN-ORG', 'MR-ORG-02', 'Active'],
    ['LE-07', 'LN-SRC-WT-ITEM', 'LN-STG-APP', null, 'Draft'],
  ];

  const lineageEdges: LineageEdge[] = lineageEdgeSeeds.map(
    ([code, fromNodeCode, toNodeCode, mappingRuleCode, status]) => ({
      id: idFor('LineageEdge', code),
      code,
      fromNodeCode,
      toNodeCode,
      mappingRuleCode,
      status,
    }),
  );

  const lineageMappings: LineageMapping[] = [
    {
      id: idFor('LineageMapping', 'MAP-APP-CANONICAL'),
      code: 'MAP-APP-CANONICAL',
      name: 'Aurora CMDB application → canonical.application',
      sourceLabel: 'Aurora CMDB — ci_application',
      targetLabel: 'canonical.application',
      mappingType: 'Source-to-Canonical',
      status: 'Published',
      ownerPersonCode: 'PPL-0013',
      createdOn: addDays(asOf, -412),
      lastModifiedAt: timestampBefore(asOf, 9, 320),
      approvedByPersonCode: 'PPL-0007',
      approvedAt: timestampBefore(asOf, 7, 120),
      version: 'v3.2',
      rules: [
        {
          code: 'MR-APP-01',
          ordinal: 1,
          ruleName: 'Business code',
          transformationType: 'Direct',
          expression: 'trim(source.ci_code) → application.businessCode',
          isEditable: false,
        },
        {
          code: 'MR-APP-02',
          ordinal: 2,
          ruleName: 'Criticality',
          transformationType: 'Lookup',
          expression: "lookup('criticality', source.bus_crit) → application.criticality",
          isEditable: true,
        },
        {
          code: 'MR-APP-03',
          ordinal: 3,
          ruleName: 'Technical health score',
          transformationType: 'Derive',
          expression: 'round(100 - (source.debt_index * 12), 0) → application.technicalHealthScore',
          isEditable: true,
        },
        {
          code: 'MR-APP-04',
          ordinal: 4,
          ruleName: 'Owner resolution',
          transformationType: 'Match',
          expression: 'match(source.owner_email, person.email) → application.ownerId',
          isEditable: true,
        },
        {
          code: 'MR-APP-05',
          ordinal: 5,
          ruleName: 'Lifecycle',
          transformationType: 'Lookup',
          expression: "lookup('lifecycle', source.status) → application.lifecycle",
          isEditable: true,
        },
        {
          code: 'MR-APP-06',
          ordinal: 6,
          ruleName: 'Default data classification',
          transformationType: 'Default',
          expression: "coalesce(source.data_class, 'Internal') → application.dataClassification",
          isEditable: true,
        },
      ],
      preview: {
        sourceColumns: ['ci_code', 'ci_name', 'bus_crit', 'status', 'owner_email'],
        targetColumns: ['businessCode', 'name', 'criticality', 'lifecycle', 'ownerId'],
        rows: [
          {
            ci_code: 'APP-0001',
            ci_name: 'Aurora PSS Core',
            bus_crit: '1',
            status: 'PROD',
            owner_email: 'mateo.guzman@auroraairlines.example',
            businessCode: 'APP-0001',
            name: 'Aurora PSS Core',
            criticality: 'MissionCritical',
            lifecycle: 'Active',
            ownerId: 'PPL-0059',
          },
          {
            ci_code: 'APP-0005',
            ci_name: 'SkyPort Departure Control',
            bus_crit: '1',
            status: 'PROD',
            owner_email: 'paulo.nogueira@auroraairlines.example',
            businessCode: 'APP-0005',
            name: 'SkyPort Departure Control',
            criticality: 'MissionCritical',
            lifecycle: 'Active',
            ownerId: 'PPL-0057',
          },
          {
            ci_code: 'APP-0023',
            ci_name: 'SlotDesk Manager',
            bus_crit: '3',
            status: 'PROD',
            owner_email: null,
            businessCode: 'APP-0023',
            name: 'SlotDesk Manager',
            criticality: 'Important',
            lifecycle: 'Active',
            ownerId: null,
          },
        ],
      },
      validation: [
        {
          severity: 'Error',
          rule: 'MR-APP-04',
          issue: 'Owner email could not be resolved to a person record.',
          count: 41,
          lastOccurredAt: timestampBefore(asOf, 0, 260),
        },
        {
          severity: 'Warning',
          rule: 'MR-APP-02',
          issue:
            'Source criticality value outside the controlled vocabulary; defaulted to Important.',
          count: 118,
          lastOccurredAt: timestampBefore(asOf, 1, 640),
        },
        {
          severity: 'Warning',
          rule: 'MR-APP-03',
          issue: 'Debt index missing; technical health left unknown.',
          count: 63,
          lastOccurredAt: timestampBefore(asOf, 1, 90),
        },
        {
          severity: 'Info',
          rule: 'MR-APP-06',
          issue: 'Data classification defaulted to Internal.',
          count: 402,
          lastOccurredAt: timestampBefore(asOf, 0, 30),
        },
      ],
      stewards: [
        { role: 'Business Steward', personCode: 'PPL-0008' },
        { role: 'Technical Steward', personCode: 'PPL-0013' },
        { role: 'Data Owner', personCode: 'PPL-0007' },
      ],
      approvalWorkflow: [
        {
          stage: 'Draft',
          personCode: 'PPL-0013',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 21, 200),
        },
        {
          stage: 'Validated',
          personCode: 'PPL-0013',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 16, 480),
        },
        {
          stage: 'Business Review',
          personCode: 'PPL-0008',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 11, 300),
        },
        {
          stage: 'Approved',
          personCode: 'PPL-0007',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 7, 120),
        },
        {
          stage: 'Published',
          personCode: 'PPL-0007',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 7, 60),
        },
      ],
    },
    {
      id: idFor('LineageMapping', 'MAP-COST-CANONICAL'),
      code: 'MAP-COST-CANONICAL',
      name: 'Finance ERP cost actual → canonical.cost_actual',
      sourceLabel: 'Finance ERP — gl_cost_actual',
      targetLabel: 'canonical.cost_actual',
      mappingType: 'Source-to-Canonical',
      status: 'Draft',
      ownerPersonCode: 'PPL-0014',
      createdOn: addDays(asOf, -96),
      lastModifiedAt: timestampBefore(asOf, 1, 140),
      approvedByPersonCode: null,
      approvedAt: null,
      version: 'v0.9',
      rules: [
        {
          code: 'MR-COST-01',
          ordinal: 1,
          ruleName: 'Cost object resolution',
          transformationType: 'Match',
          expression: 'match(source.cost_centre, cost_object.code) → costActual.costObjectId',
          isEditable: true,
        },
        {
          code: 'MR-COST-02',
          ordinal: 2,
          ruleName: 'Amount normalisation',
          transformationType: 'Parse',
          expression: 'to_decimal(source.amount, 2) * fx_rate(source.currency) → costActual.amount',
          isEditable: true,
        },
        {
          code: 'MR-COST-03',
          ordinal: 3,
          ruleName: 'Category derivation',
          transformationType: 'Derive',
          expression:
            "case source.account_group when '61' then 'Run' when '62' then 'Enhance' else 'Other' end",
          isEditable: true,
        },
      ],
      preview: {
        sourceColumns: ['cost_centre', 'account_group', 'amount', 'currency', 'period'],
        targetColumns: ['costObjectCode', 'category', 'amount', 'currency', 'fiscalYear'],
        rows: [
          {
            cost_centre: 'CC-40182',
            account_group: '61',
            amount: '1284100.00',
            currency: 'USD',
            period: '2026-04',
            costObjectCode: 'APP-0013',
            category: 'Run',
            amount_out: '1284100.00',
            currency_out: 'USD',
            fiscalYear: '2026',
          },
          {
            cost_centre: 'CC-40990',
            account_group: '62',
            amount: '498200.00',
            currency: 'EUR',
            period: '2026-04',
            costObjectCode: null,
            category: 'Enhance',
            amount_out: '536880.00',
            currency_out: 'USD',
            fiscalYear: '2026',
          },
        ],
      },
      validation: [
        {
          severity: 'Error',
          rule: 'MR-COST-01',
          issue: 'Cost centre has no mapped cost object; rows held in the error queue.',
          count: 612,
          lastOccurredAt: timestampBefore(asOf, 3, 40),
        },
        {
          severity: 'Warning',
          rule: 'MR-COST-02',
          issue: 'FX rate older than the posting period.',
          count: 88,
          lastOccurredAt: timestampBefore(asOf, 3, 55),
        },
      ],
      stewards: [
        { role: 'Business Steward', personCode: 'PPL-0058' },
        { role: 'Technical Steward', personCode: 'PPL-0014' },
        { role: 'Data Owner', personCode: null },
      ],
      approvalWorkflow: [
        {
          stage: 'Draft',
          personCode: 'PPL-0014',
          status: 'Complete',
          occurredAt: timestampBefore(asOf, 12, 90),
        },
        { stage: 'Validated', personCode: null, status: 'Blocked', occurredAt: null },
        { stage: 'Business Review', personCode: null, status: 'Pending', occurredAt: null },
        { stage: 'Approved', personCode: null, status: 'Pending', occurredAt: null },
        { stage: 'Published', personCode: null, status: 'Pending', occurredAt: null },
      ],
    },
  ];

  // ── Audit trail ────────────────────────────────────────────────────────────
  const auditEvents: AuditEvent[] = [];
  const auditTemplates: readonly [string, string, string][] = [
    [
      'Approved classification',
      'Assessment ASM-0001',
      'Crew Disruption Recovery classified as Business Product.',
    ],
    [
      'Published mapping set',
      'MAP-2026-Q2 v4',
      'Process-to-product mapping published; 1,284 relationships affected.',
    ],
    [
      'Updated allocation',
      'APP-0037 → PRD-C360',
      'Allocation share changed from 0.35 to 0.42 with a stated reason.',
    ],
    [
      'Raised data quality issue',
      'DQ-004',
      'Applications without an approved product mapping: 139.',
    ],
    [
      'Returned assessment',
      'Assessment ASM-0002',
      'Returned for boundary split before classification.',
    ],
    [
      'Changed product owner',
      'PRD-BAGJ',
      'Business owner reassigned following the Q2 reorganisation.',
    ],
    ['Paused connector', 'INT-012 Notification Service', 'Paused pending credential rotation.'],
    [
      'Recorded source conflict',
      'APP-0005 annualRunCost',
      'Aurora CMDB and Finance ERP disagree by USD 2.54M.',
    ],
    [
      'Exported portfolio view',
      '/portfolio/hierarchy',
      'Export requested with the enterprise scope and FY2026 period.',
    ],
    ['Recalculated scenario', 'SCN-TARGET', 'Assumptions changed; comparison recalculated.'],
  ];
  for (let i = 0; i < 60; i += 1) {
    const template = at(auditTemplates, i % auditTemplates.length);
    const actor = at(
      ['PPL-0008', 'PPL-0013', 'PPL-0005', 'PPL-0046', 'PPL-0015', 'PPL-0007'],
      i % 6,
    );
    auditEvents.push({
      id: idFor('AuditEvent', `AUD-${pad(i + 1, 5)}`),
      occurredAt: timestampBefore(asOf, Math.floor(i / 3), rng.int(0, 1400)),
      actorPersonCode: actor,
      action: at(template, 0),
      entityLabel: at(template, 1),
      detail: at(template, 2),
      correlationId: correlationIdFor('audit', actor, i),
    });
  }

  return {
    evidence,
    assessments,
    decisions,
    approvals,
    auditEvents,
    dataQualityRules,
    dataQualityIssues,
    sourceConflicts,
    mappingSets,
    connectors,
    ingestionRuns,
    lineageNodes,
    lineageEdges,
    lineageMappings,
  };
}

/** Weighted total for a set of criterion scores, on the 0–100 scale. */
export function weightedScoreOutOf100(scores: readonly AssessmentCriterionScore[]): number {
  let total = 0;
  for (const criterion of ASSESSMENT_CRITERIA) {
    const score = scores.find((s) => s.criterionId === criterion.criterionId)?.score ?? null;
    if (score === null) continue;
    total += (score / 5) * criterion.weightPercent;
  }
  return round(total, 1);
}
